import { END_NODE_ID_PREFIX } from "@bliks-io/contracts";
// Mongo ObjectId shape (24 hex chars), used for validating id tails extracted
// from synthetic node/handle ids before they flow into backend mutations.
export const OBJECT_ID_RE = /^[a-f\d]{24}$/i;
// Node dimension constants
export const NODE_WIDTH = 180;
export const NODE_HEIGHT = 90;
export const GATEWAY_SIZE = 60;
export const GATEWAY_NODE_HEIGHT = 90; // Diamond + label below
export const START_END_WIDTH = 60;
export const START_END_HEIGHT = 60;
// Inset the visible circle 2px inside the node box so the Tailwind `ring-*`
// selection overlay paints along the circle's bounding box (rather than
// overflowing into a square) and leaves room for React Flow's edge handle.
export const START_END_CIRCLE_INSET = 2;
export const START_END_CIRCLE_SIZE = START_END_HEIGHT - START_END_CIRCLE_INSET * 2;
export const START_END_HANDLE_Y = START_END_HEIGHT / 2;
export const START_END_LABEL_Y = START_END_HEIGHT + 2;
// ELK's horizontal gap between layers (columns) of the auto-layout.
export const LAYER_SPACING = 200;
// Swimlane layout constants
export const SWIMLANE_LABEL_WIDTH = 120;
export const SWIMLANE_PADDING_Y = 60;
export const SWIMLANE_PADDING_X = 60;
export const SWIMLANE_GAP = 24;
export const SWIMLANE_MIN_HEIGHT = NODE_HEIGHT + SWIMLANE_PADDING_Y * 2;
// Stable empty arrays to avoid re-renders in ReactFlow components
export const EMPTY_NODES = [];
export const EMPTY_EDGES = [];
// Event node id prefix, shared with the backend layout store — don't change
// without a data migration on `ProcessLayout.generatedNodePositions.nodeId`.
export const EVENT_NODE_ID_PREFIX = "event-";
export const FLOW_EVENT_NODE_TYPE = "flowEvent";
export function buildEventNodeId(eventId) {
    return `${EVENT_NODE_ID_PREFIX}${eventId}`;
}
export function isEventNodeId(nodeId) {
    return nodeId.startsWith(EVENT_NODE_ID_PREFIX);
}
// Returns the canonical event id for `event-<eventId>` (tail is a bare 24-char
// ObjectId). Returns null for the legacy `event-<stepId>-<inputIdx>` form —
// those aren't addressable via the events endpoints since they have no
// standalone event entity yet; callers must decide how to handle them.
export function parseEventNodeId(nodeId) {
    if (!isEventNodeId(nodeId)) return null;
    const tail = nodeId.slice(EVENT_NODE_ID_PREFIX.length);
    return OBJECT_ID_RE.test(tail) ? tail : null;
}
export function isEndNodeId(nodeId) {
    return nodeId.startsWith(END_NODE_ID_PREFIX);
}
// Gateway node id prefix. Two shapes share this prefix:
//   `gateway-<stepId>`                 — task-fork `+` synthesized when a task
//                                         step fans out to ≥2 consumers.
//   `gateway-<stepId>-rule-<ruleId>`   — rule-scoped parallel gateway
//                                         synthesized when a decision rule
//                                         output fans out to ≥2 consumers.
// Both tail ids are 24-hex ObjectIds, so the composite shape parses
// unambiguously against the plain one.
export const GATEWAY_NODE_ID_PREFIX = "gateway-";
const RULE_SCOPED_GATEWAY_RE = /^([a-f\d]{24})-rule-([a-f\d]{24})$/i;
export function buildGatewayNodeId(stepId, ruleId) {
    return ruleId ? `${GATEWAY_NODE_ID_PREFIX}${stepId}-rule-${ruleId}` : `${GATEWAY_NODE_ID_PREFIX}${stepId}`;
}
export function isGatewayNodeId(nodeId) {
    return nodeId.startsWith(GATEWAY_NODE_ID_PREFIX);
}
export function parseGatewayNodeId(nodeId) {
    if (!isGatewayNodeId(nodeId)) return null;
    const tail = nodeId.slice(GATEWAY_NODE_ID_PREFIX.length);
    // Fast path: plain `gateway-<24-hex>` skips regex — by far the common case
    // on the `classifyNodeEnd` hot path (every edge during classification).
    if (tail.length === 24) return {
        stepId: tail
    };
    const ruleScoped = tail.match(RULE_SCOPED_GATEWAY_RE);
    if (ruleScoped) return {
        stepId: ruleScoped[1],
        ruleId: ruleScoped[2]
    };
    return {
        stepId: tail
    };
}

//# sourceMappingURL=constants.js.map